// GOAL DOMAIN cell tags - single source of truth for every page that shows a
// goal's domain: GoalsPage, ClientHistory and the Goal Review page.
//
// `goalDomain` holds the list of domains and `goalSkills` the skills parallel
// to it. A goal with no skill of its own shows the domain alone - never
// "Play and Cognition - Play and Cognition".

// Comparison form of a domain / skill label.
//
// `&` and "and" are the SAME word in this catalogue: `intervention_data` ships
// both spellings of the same domain (115 "Communication and Social" rows AND a
// "Communication & Social" block), so a goal saved across the two spellings
// printed its one domain twice under two spellings. Normalising before
// comparing is what makes them one domain. Identical to normalizeDomain() in
// goalsetdownload.js / goalreview.js and normalizeName() in UpdateGoal.js - all
// four have to agree or the same goal reads differently on different pages.
const normalizeDomain = (s) =>
  String(s == null ? "" : s)
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]/g, "");

const buildDomainTags = (row) => {
  const domains = Array.isArray(row?.goalDomain)
    ? row.goalDomain
    : String(row?.goalDomain || "")
        .split(",")
        .map((d) => d.trim())
        .filter(Boolean);

  const skills = Array.isArray(row?.goalSkills) ? row.goalSkills : [];

  // Resolve the (domain, skill) pairs FIRST, so a domain that turns out to have
  // a real skill can shed its bare twin afterwards. Doing it in one pass could
  // not know that yet.
  const pairs = [];
  const seenPairs = new Set();

  domains.forEach((raw, i) => {
    const domain = String(raw || "").trim();
    if (!domain) return;

    let skill = String(skills[i] == null ? "" : skills[i]).trim();
    if (!skill || skill.toLowerCase() === "null") skill = "";

    const normDomain = normalizeDomain(domain);
    const normSkill = normalizeDomain(skill);
    // A domain repeated as its own skill carries no extra information.
    // Compared NORMALISED, so "Play & Cognition" + "Play and Cognition" now
    // collapses to the bare domain exactly as "Play and Cognition" +
    // "Play and Cognition" always did - the exact comparison above let the
    // "&" spelling through as a bogus "Play & Cognition - Play and Cognition".
    const effectiveSkill = normSkill && normSkill === normDomain ? "" : skill;
    if (!normDomain && !normalizeDomain(effectiveSkill)) return;

    const key = `${normDomain}||${normalizeDomain(effectiveSkill)}`;
    if (seenPairs.has(key)) return;
    seenPairs.add(key);
    pairs.push({ domain, skill: effectiveSkill, normDomain });
  });

  // A goal can legitimately cover BOTH a specific skill and that same domain's
  // generic no-skill row: `intervention_data` has 115 "Communication and
  // Social" rows with goal_skill = NULL, and UpdateGoal's domain dropdown
  // offers both. Selecting both stored
  //   domains ["Communication and Social","Communication and Social"]
  //   skills  ["Expressive Language","Communication and Social"]
  // (goal 310, session 100056) which rendered as
  //   "Communication and Social - Expressive Language, Communication and Social"
  // The bare twin names a domain the skilled tag has already named, so it adds
  // nothing and reads as a duplicate. Drop it.
  //
  // Only the BARE entry is dropped. Distinct skills under one domain are real
  // and all stay - goal 212 has four different Participation skills and needs
  // all four listed.
  const domainsWithASkill = new Set(
    pairs.filter((p) => p.skill).map((p) => p.normDomain)
  );

  // One SPELLING per domain.
  //
  // The pathway options - what the therapist actually picks from, and what every
  // downstream question stores - only ever spell a domain with "and":
  // `pathway_question_options` holds "Communication and Social" and "Play and
  // Cognition", 31 each, and no "&" variant of either. The "&" spelling comes
  // only from `intervention_data`, which ships both, and it reaches
  // pathway_goals through whichever catalogue group a goal was built from. A
  // goal spanning both spellings (goal 205:
  // ["Communication and Social","Play & Cognition","Communication & Social",
  // "Communication & Social"]) therefore printed ONE domain three times under
  // two spellings. Prefer the spelling the pathway uses.
  //
  // A domain that only ever appears with "&" (goal 207, "Eating & Drinking")
  // keeps it - there is nothing better to pick, and inventing a spelling would
  // be a guess. A goal whose domains are all spelled one way is untouched.
  const spellingByDomain = new Map();
  for (const p of pairs) {
    const seen = spellingByDomain.get(p.normDomain);
    if (seen === undefined || (seen.includes("&") && !p.domain.includes("&"))) {
      spellingByDomain.set(p.normDomain, p.domain);
    }
  }

  return pairs
    .filter((p) => p.skill || !domainsWithASkill.has(p.normDomain))
    .map((p) => {
      const domain = spellingByDomain.get(p.normDomain) || p.domain;
      return p.skill ? `${domain} - ${p.skill}` : domain;
    });
};

// Accepts a goal exactly as the API returns it (goal_domain / goal_skill) and
// returns the same tag list, so the review page does not have to reshape it.
const buildDomainTagsFromGoal = (goal) =>
  buildDomainTags({ goalDomain: goal?.goal_domain, goalSkills: goal?.goal_skill });

// Accepts the intervention-catalogue shape the summary "Goals:" row is built
// from: `data` keyed by catalogue group, each group an array of intervention
// rows carrying the goal's own goal_domain / goal_skill. The group key is only
// a fallback for a missing domain.
//
// This existed three times verbatim - Interventions.js, goalreview.js and
// goalsetdownload.js each inlined
//
//   Object.keys(map).map(key => {
//     const first = (map[key] || [])[0] || {};
//     const domain = first.goal_domain || key || "";
//     const skill = first.goal_skill || "";
//     return skill && skill !== "null" ? `${domain} - ${skill}` : domain;
//   })
// then `[...new Set(...)]`,
//
// which dedupes on the exact rendered string only. A goal whose catalogue groups
// are "Communication and Social" + "Expressive language" and a bare
// "Communication and Social" (goal_skill = NULL, 115 such rows in
// intervention_data) therefore printed its one domain twice, while every Goals
// TABLE showed it once. Routing through buildDomainTags() makes the summary row
// obey the same four rules as the tables, from one implementation.
//
// Labels only. The goal TABS in Interventions.js are built separately and must
// keep one tab per catalogue group - a tab owns its group's interventions, so
// dropping the bare group's tab would hide real interventions.
const buildDomainTagsFromDataMap = (map) => {
  const groups = map && typeof map === "object" ? Object.keys(map) : [];
  const domains = [];
  const skills = [];

  groups.forEach((key) => {
    const items = Array.isArray(map[key]) ? map[key] : [];
    const first = items[0] || {};
    const skill = first.goal_skill;
    domains.push(first.goal_domain || key || "");
    // "null" is a stored string here, not a missing value.
    skills.push(skill && String(skill) !== "null" ? String(skill) : "");
  });

  return buildDomainTags({ goalDomain: domains, goalSkills: skills });
};

export { buildDomainTags, buildDomainTagsFromGoal, buildDomainTagsFromDataMap };
export default buildDomainTags;
